/* Compliance Overview dashboard — iter61.
 * 12-month executive view of the payroll signature campaign.
 * Route: /compliance-overview   (HR / admin / director / comptable).
 */
import { useEffect, useState } from 'react';
import { biometricApi } from '../lib/api';
import { toast } from 'sonner';
import { Toaster } from '../components/ui/sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, ComposedChart,
} from 'recharts';
import {
  ChartLine, CurrencyDollarSimple, ArrowClockwise, FileText, EnvelopeSimple,
  Warning, TrendUp,
} from '@phosphor-icons/react';

const fmtMonth = (m) => {
  if (!m) return '—';
  try {
    const [y, mo] = m.split('-');
    return new Date(Number(y), Number(mo) - 1, 1).toLocaleDateString('fr-FR',
      { month: 'short', year: '2-digit' });
  } catch { return m; }
};
const fmtAmount = (v) => (v || 0).toLocaleString('fr-FR') + ' F';

export default function ComplianceOverview() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [months, setMonths] = useState(12);
  const [emailBusy, setEmailBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await biometricApi.complianceOverview(months);
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur chargement');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [months]);   // load re-created each render

  const emailLastMonth = async () => {
    setEmailBusy(true);
    try {
      const { data } = await biometricApi.generateCompliance(null, true);
      const n = data.notified?.sent ?? 0;
      toast.success(`Rapport ${data.key} envoyé à ${n} destinataire(s)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur envoi email');
    } finally { setEmailBusy(false); }
  };

  const kpi = data?.kpi;

  return (
    <div className="p-6 space-y-6" data-testid="compliance-overview-page">
      <Toaster position="top-center" richColors />
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-outfit font-bold text-[#0F172A] flex items-center gap-2">
            <ChartLine size={28} weight="duotone" className="text-[#0052FF]" />
            Compliance Overview
          </h1>
          <p className="text-sm text-[#64748B]">
            Vision 12 mois de la campagne de signature paie — signés, en attente, masse salariale et récurrents non-signants.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select value={months} onChange={(e) => setMonths(Number(e.target.value))}
            className="h-9 rounded-md border border-[#E2E8F0] px-2 text-sm bg-white"
            data-testid="months-selector">
            <option value={3}>3 derniers mois</option>
            <option value={6}>6 derniers mois</option>
            <option value={12}>12 derniers mois</option>
            <option value={24}>24 derniers mois</option>
          </select>
          <Button variant="outline" onClick={load} data-testid="refresh-btn">
            <ArrowClockwise size={14} className="mr-1" /> Rafraîchir
          </Button>
          <a
            href={biometricApi.complianceOverviewXlsxUrl(months)}
            className="inline-flex items-center h-9 px-3 rounded-md border border-emerald-400 text-emerald-700 hover:bg-emerald-50 text-sm font-semibold"
            data-testid="export-xlsx-btn"
          >
            <FileText size={14} className="mr-1" /> Export Excel
          </a>
          <Button onClick={emailLastMonth} disabled={emailBusy}
            className="bg-[#0052FF] hover:bg-[#0043D1]"
            data-testid="email-report-btn">
            <EnvelopeSimple size={14} className="mr-1" />
            {emailBusy ? 'Envoi…' : 'Envoyer rapport mois dernier'}
          </Button>
        </div>
      </div>

      {loading ? (
        <p className="text-center text-[#94A3B8] py-12">Chargement…</p>
      ) : !data ? (
        <p className="text-center text-red-600 py-12">Données indisponibles.</p>
      ) : (
        <>
          {/* iter65 — Verdict card: 3-second executive summary */}
          {data.forecast && data.forecast.length > 0 && (() => {
            const nextRate = data.forecast[0]?.predicted_rate ?? 0;
            const recCount = data.recurring_pending?.length ?? 0;
            // Verdict logic: rate ≥ 80% AND ≤ 2 recurring → green, rate ≥ 50%
            // OR ≤ 5 recurring → amber, else red.
            let verdict, color, bg, border, msg, icon;
            if (nextRate >= 80 && recCount <= 2) {
              verdict = 'En bonne voie';
              color = '#059669'; bg = '#ECFDF5'; border = '#A7F3D0';
              icon = '🟢';
              msg = `Prévision ${nextRate}% pour ${fmtMonth(data.forecast[0].month)} — continuez comme ça.`;
            } else if (nextRate >= 50 && recCount <= 5) {
              verdict = 'Risque modéré';
              color = '#B45309'; bg = '#FFFBEB'; border = '#FCD34D';
              icon = '🟡';
              msg = `Prévision ${nextRate}% + ${recCount} employé(s) récurrent(s). Anticipez la relance.`;
            } else {
              verdict = 'Action requise';
              color = '#B91C1C'; bg = '#FEF2F2'; border = '#FECACA';
              icon = '🔴';
              msg = `Prévision ${nextRate}% + ${recCount} récurrent(s). Lancez une campagne de relance immédiate.`;
            }
            return (
              <div
                data-testid="verdict-card"
                data-verdict={verdict}
                style={{ background: bg, border: `2px solid ${border}` }}
                className="rounded-xl p-5 flex items-start gap-4"
              >
                <div className="text-4xl leading-none" aria-hidden="true">{icon}</div>
                <div className="flex-1">
                  <p className="text-[10px] uppercase tracking-wide font-bold" style={{ color }}>
                    Prévision fin de mois
                  </p>
                  <h2 className="text-2xl font-outfit font-bold mt-1" style={{ color }}>
                    {verdict}
                  </h2>
                  <p className="text-sm mt-1" style={{ color: color + 'CC' }}>{msg}</p>
                </div>
                <div className="text-right">
                  <p className="text-[10px] uppercase tracking-wide text-[#64748B]">
                    {fmtMonth(data.forecast[0].month)}
                  </p>
                  <p className="text-3xl font-outfit font-bold" style={{ color }}
                    data-testid="verdict-predicted-rate">
                    {nextRate}%
                  </p>
                </div>
              </div>
            );
          })()}

          {/* KPI ROW */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4">
                <p className="text-[10px] uppercase tracking-wide text-[#64748B] font-bold">
                  Taux moyen de signature
                </p>
                <p className="text-3xl font-outfit font-bold text-[#0052FF]"
                  data-testid="kpi-avg-rate">
                  {kpi.avg_signature_rate}%
                </p>
                <p className="text-[10px] text-[#94A3B8]">sur {kpi.months_analyzed} mois</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-[10px] uppercase tracking-wide text-[#64748B] font-bold">
                  Masse salariale cumulée
                </p>
                <p className="text-3xl font-outfit font-bold text-emerald-700 flex items-center gap-1"
                  data-testid="kpi-total-payroll">
                  <CurrencyDollarSimple size={22} weight="duotone" />
                  {fmtAmount(kpi.total_payroll)}
                </p>
                <p className="text-[10px] text-[#94A3B8]">total sur la période</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-[10px] uppercase tracking-wide text-emerald-700 font-bold flex items-center gap-1">
                  <TrendUp size={12} weight="bold" /> Meilleur mois
                </p>
                <p className="text-2xl font-outfit font-bold text-emerald-700"
                  data-testid="kpi-best-month">
                  {fmtMonth(kpi.best_month)}
                </p>
                <p className="text-[10px] text-emerald-700/70">{kpi.best_rate}% signés</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-[10px] uppercase tracking-wide text-red-700 font-bold flex items-center gap-1">
                  <Warning size={12} weight="bold" /> Pire mois
                </p>
                <p className="text-2xl font-outfit font-bold text-red-700"
                  data-testid="kpi-worst-month">
                  {fmtMonth(kpi.worst_month)}
                </p>
                <p className="text-[10px] text-red-700/70">{kpi.worst_rate}% signés</p>
              </CardContent>
            </Card>
          </div>

          {/* LINE CHART — signature rate evolution + 3-month forecast */}
          <Card>
            <CardContent className="p-4">
              <h3 className="font-outfit font-semibold mb-2 flex items-center gap-2">
                <ChartLine size={18} weight="duotone" className="text-[#0052FF]" />
                Taux de signature (évolution + prévision 3 mois)
              </h3>
              <div style={{ width: '100%', height: 260, minWidth: 300 }}>
                <ResponsiveContainer>
                  <LineChart data={[
                    ...data.months.map((m) => ({
                      label: fmtMonth(m.month),
                      historique: m.signature_rate,
                      prevision: null,
                    })),
                    ...(data.forecast || []).map((f, idx) => ({
                      label: fmtMonth(f.month) + ' *',
                      // Bridge the last historical point to the first
                      // predicted point so the dotted line connects.
                      historique: idx === 0 && data.months.length
                        ? data.months[data.months.length - 1].signature_rate
                        : null,
                      prevision: f.predicted_rate,
                    })),
                  ]} margin={{ top: 8, right: 20, bottom: 8, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                    <YAxis unit="%" domain={[0, 100]} tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v) => v == null ? '—' : `${v}%`} />
                    <Legend />
                    <Line type="monotone" dataKey="historique" stroke="#0052FF"
                      strokeWidth={2.5} name="Historique" dot={{ r: 4 }}
                      connectNulls />
                    <Line type="monotone" dataKey="prevision" stroke="#F59E0B"
                      strokeWidth={2} strokeDasharray="5 5"
                      name="Prévision (régression linéaire)"
                      dot={{ r: 4, fill: '#F59E0B' }} connectNulls />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              {data.forecast && data.forecast.length > 0 && (
                <p className="text-[10px] text-[#94A3B8] mt-1">
                  * Prévision basée sur la tendance linéaire des mois actifs.
                  À interpréter comme un indicateur de trajectoire, pas un engagement.
                </p>
              )}
            </CardContent>
          </Card>

          {/* COMPOSED CHART — payroll (bars) + rate (line) */}
          <Card>
            <CardContent className="p-4">
              <h3 className="font-outfit font-semibold mb-2 flex items-center gap-2">
                <CurrencyDollarSimple size={18} weight="duotone" className="text-emerald-600" />
                Masse salariale et taux
              </h3>
              <div style={{ width: '100%', height: 300, minWidth: 300 }}>
                <ResponsiveContainer>
                  <ComposedChart data={data.months.map((m) => ({
                    ...m, label: fmtMonth(m.month),
                    pending_amount: m.total_amount - m.signed_amount,
                  }))} margin={{ top: 8, right: 20, bottom: 8, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                    <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
                    <YAxis yAxisId="right" orientation="right" domain={[0, 100]}
                      unit="%" tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v, k) => k.includes('rate')
                      ? `${v}%` : fmtAmount(v)} />
                    <Legend />
                    <Bar yAxisId="left" dataKey="signed_amount" stackId="a"
                      fill="#059669" name="Signés (F)" />
                    <Bar yAxisId="left" dataKey="pending_amount" stackId="a"
                      fill="#F59E0B" name="En attente (F)" />
                    <Line yAxisId="right" type="monotone" dataKey="signature_rate"
                      stroke="#0052FF" strokeWidth={2} name="Taux signature (%)"
                      dot={{ r: 3 }} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Recurring non-signers table */}
          <Card>
            <CardContent className="p-4">
              <h3 className="font-outfit font-semibold mb-3 flex items-center gap-2">
                <Warning size={18} weight="duotone" className="text-amber-600" />
                Employés récurrents en attente ({data.recurring_pending.length})
              </h3>
              {data.recurring_pending.length === 0 ? (
                <p className="text-sm text-[#94A3B8] italic text-center py-6">
                  Aucun employé récurrent — parfait !
                </p>
              ) : (
                <table className="w-full text-sm" data-testid="recurring-table">
                  <thead>
                    <tr className="border-b border-[#E2E8F0]">
                      <th className="text-left py-2 px-2 font-semibold text-[#64748B]">Employé</th>
                      <th className="text-center py-2 px-2 font-semibold text-[#64748B]">Mois non-signés</th>
                      <th className="text-left py-2 px-2 font-semibold text-[#64748B]">Périodes concernées</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recurring_pending.map((r) => (
                      <tr key={r.employee_id} className="border-b border-[#F1F5F9]"
                        data-testid={`recurring-row-${r.employee_id}`}>
                        <td className="py-2 px-2 font-medium">{r.employee_name}</td>
                        <td className="py-2 px-2 text-center">
                          <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-bold ${
                            r.unsigned_count >= 4
                              ? 'bg-red-100 text-red-700'
                              : 'bg-amber-100 text-amber-800'
                          }`}>
                            {r.unsigned_count}
                          </span>
                        </td>
                        <td className="py-2 px-2 text-xs text-[#64748B]">
                          {r.periods.slice(0, 5).map(fmtMonth).join(', ')}
                          {r.periods.length > 5 ? ` +${r.periods.length - 5}` : ''}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>

          {/* Detailed monthly table */}
          <Card>
            <CardContent className="p-4">
              <h3 className="font-outfit font-semibold mb-3 flex items-center gap-2">
                <FileText size={18} weight="duotone" className="text-[#0052FF]" />
                Détail mensuel
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#E2E8F0]">
                      <th className="text-left py-2 px-2 font-semibold text-[#64748B]">Mois</th>
                      <th className="text-center py-2 px-2 font-semibold text-[#64748B]">Total</th>
                      <th className="text-center py-2 px-2 font-semibold text-[#64748B]">Signés</th>
                      <th className="text-center py-2 px-2 font-semibold text-[#64748B]">Taux</th>
                      <th className="text-center py-2 px-2 font-semibold text-[#64748B]">Relance J+2/J+5</th>
                      <th className="text-right py-2 px-2 font-semibold text-[#64748B]">Masse salariale</th>
                      <th className="text-center py-2 px-2 font-semibold text-[#64748B]">PDF</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.months.slice().reverse().map((m) => (
                      <tr key={m.month} className="border-b border-[#F1F5F9]">
                        <td className="py-2 px-2 font-medium">{fmtMonth(m.month)}</td>
                        <td className="py-2 px-2 text-center">{m.total}</td>
                        <td className="py-2 px-2 text-center text-emerald-700 font-semibold">
                          {m.signed}
                        </td>
                        <td className="py-2 px-2 text-center">
                          <span className={`font-bold ${
                            m.signature_rate >= 80 ? 'text-emerald-700'
                              : m.signature_rate >= 50 ? 'text-amber-700'
                              : 'text-red-700'
                          }`}>
                            {m.signature_rate}%
                          </span>
                        </td>
                        <td className="py-2 px-2 text-center text-xs text-amber-700">
                          {m.reminded_j2} / {m.reminded_j5}
                        </td>
                        <td className="py-2 px-2 text-right font-mono text-xs">
                          {fmtAmount(m.total_amount)}
                        </td>
                        <td className="py-2 px-2 text-center">
                          {m.total > 0 && (
                            <a
                              href={biometricApi.complianceReportUrl(m.month)}
                              target="_blank" rel="noopener noreferrer"
                              className="text-[#0052FF] hover:underline text-xs font-semibold"
                              data-testid={`pdf-link-${m.month}`}
                            >
                              PDF
                            </a>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
